/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {Asset} from '@ion/core/src/types/entities';
import EntityObserver from './EntityObserver';
import {
  EntitySubscription,
  EntityUpdate,
  ObservableEntityIds,
  OnDataEvent
} from './types';

/**
 * The AssetObserver API allows for receiving notifications for all changes for
 * a given asset, or collection of assets. This API essentially delegates to the
 * EntityObserver API, only in the context of Assets only.
 *
 * Note, while initialization of the EntityObserver is generally performed during
 * application initialization, as a best practice, always add observers via the
 * EntityObserver.onInitialized method, which will invoke the provided callback
 * once initialized, or immediately if already initialized:
 *
 * ```typescript
 *
 *   import EntityObserver, {AssetObserver} from '@ion/services/src/websocket';
 *
 *   EntityObserver.onInitialized(() => {
 *     AssetObserver.addObserver(asset.id, onAssetUpdate);
 *   });
 *
 * ```
 * Once an Asset (or collection of Asset) no longer need to be observed, make sure
 * to invoke removeObserver ...
 *
 * ```typescript
 *
 *   AssetObserver.removeObserver(asset.id, onAssetUpdate);
 *
 * ```
 */
export default class AssetObserver {
  /**
   * The addObserver method allows for observing an Asset or collection of Assets
   * for changes.
   *
   * ```typescript
   *
   *   import EntityObserver, {AssetObserver} from '@ion/services/src/websocket';
   *
   *   EntityObserver.onInitialized(() => {
   *     AssetObserver.addObserver(asset.id, onAssetUpdate);
   *   });
   *
   * ```
   * @param   ids Either a single id, or array of asset ids to be observed for
   *          changes.
   * @param   observer Callback function which will be invoked upon receiving
   *          changes for the observed assets.
   * @returns true if the observer has been added, otherwise, false. Generally,
   *          an observer will only ever fail to be added if the connection
   *          has been closed or has not yet been established.
   */
  static addObserver(ids: ObservableEntityIds, observer: OnDataEvent) {
    return EntityObserver.subscribe(
      EntitySubscription.asset, ids, observer
    );
  }

  /**
   * The removeObserver method allows for removing an Asset or collection of Assets
   * from being observed for changes. This method must always be invoked when an
   * observer is no longer needed.
   *
   * ```typescript
   *
   *   import EntityObserver, {AssetObserver} from '@ion/services/src/websocket';
   *
   *   AssetObserver.removeObserver(asset.id, onAssetUpdate);
   *
   * ```
   *
   * @param   ids Either a single id, or array of asset ids to be observed for
   *          changes.
   * @param   observer Callback function which will be invoked upon receiving
   *          changes for the observed assets.
   * @returns true if the observer has been removed, otherwise, false. Generally,
   *          an observer will only ever fail to be removed if the connection
   *          has been closed or has not yet been established.
   */
  static removeObserver(ids: ObservableEntityIds, observer: OnDataEvent): any {
    return EntityObserver.unsubscribe(
      EntitySubscription.asset, ids, observer
    );
  }

  /**
   * Provides a utility which allows for removing all asset observers within
   * an application. Note, this method should be used with caution and is
   * generally only to be invoked upon logout.
   *
   * ```typescript
   *
   *   import {AssetObserver} from '@ion/services/src/websocket';
   *
   *   // unsubscribe and remove all assets from being observed ...
   *   AssetObserver.removeAllObservers();
   *
   * ```
   *
   * @returns true if the observer has been removed, otherwise, false. Generally,
   *          an observer will only ever fail to be removed if the connection
   *          has been closed or has not yet been established.
   */
  static removeAllObservers(): boolean {
    return EntityObserver.removeAllSubscriptions(EntitySubscription.asset);
  }

  /**
   * Provide a utility which updates the current state of the observed, in memory
   * Asset with the changes from received for the realtime services.
   *
   * ```typescript
   *
   *   AssetObserver.addObserver(asset.id, (changes: EntityUpdate) => {
   *      dispatch(assetUpdated(AssetObserver.update(asset, changes)));
   *   });
   *
   * ```
   *
   * @param   asset The asset being observed for changes (via `addObserver(id)`).
   * @param   updates The latest updates for the observed Asset;
   * @returns An Asset entity based on the current state of observed Asset with
   *          all changes applied.
   */
  static update(asset: Asset, updates: EntityUpdate): Asset {
    const latest = {...asset};
    Object.entries(updates).forEach(([attribute, val]) => {
      latest[attribute] = val?.data?.value ?? val?.data ?? val;
    });
    return latest;
  }
}
